import React from 'react';
import LandingNav from './components/LandingNav';
import LandingFooter from './components/LandingFooter';
import './landing.scss';

const PrivacyPage = () => (
  <div className="landing-page">
    <LandingNav />

    <div className="policy-hero">
      <div className="policy-hero-inner">
        <span className="policy-badge">Legal</span>
        <h1>Privacy Policy</h1>
        <p className="policy-meta">Last updated June 01, 2026</p>
      </div>
    </div>

    <div className="policy-content">
      <div className="policy-inner">

        <p>
          This Privacy Notice for <strong>EPO POWER AND CONTROL TRADING</strong> ("we," "us," or "our") describes
          how and why we might access, collect, store, use, and/or share ("process") your personal information
          when you use our services ("Services"), including when you:
        </p>
        <ul>
          <li>Visit our website at manzilismart.com or any website of ours that links to this Privacy Notice</li>
          <li>Download and use our mobile application (Manzilismart), or any other application of ours that links to this Privacy Notice</li>
          <li>Use Manzilismart platform. Manzili Smart bridges Siemens Desigo Optic's professional-grade building intelligence with a clean, intuitive mobile experience — giving residents full control of their environment, from anywhere.</li>
          <li>Engage with us in other related ways, including any marketing or events</li>
        </ul>
        <p>
          Questions or concerns? Reading this Privacy Notice will help you understand your privacy rights and choices.
          We are responsible for making decisions about how your personal information is processed. If you do not agree
          with our policies and practices, please do not use our Services. If you still have any questions or concerns,
          please contact us at <a href="mailto:info@manzilismart.com">info@manzilismart.com</a>.
        </p>

        <div className="policy-toc">
          <h3>Table of Contents</h3>
          <ol>
            <li><a href="#section-1">What Information Do We Collect?</a></li>
            <li><a href="#section-2">How Do We Process Your Information?</a></li>
            <li><a href="#section-3">When and With Whom Do We Share Your Personal Information?</a></li>
            <li><a href="#section-4">How Long Do We Keep Your Information?</a></li>
            <li><a href="#section-5">How Do We Keep Your Information Safe?</a></li>
            <li><a href="#section-6">Do We Collect Information From Minors?</a></li>
            <li><a href="#section-7">What Are Your Privacy Rights?</a></li>
            <li><a href="#section-8">Controls for Do-Not-Track Features</a></li>
            <li><a href="#section-9">Do We Make Updates to This Notice?</a></li>
            <li><a href="#section-10">How Can You Contact Us About This Notice?</a></li>
            <li><a href="#section-11">How Can You Review, Update, or Delete the Data We Collect From You?</a></li>
          </ol>
        </div>

        <div className="policy-section" id="section-1">
          <h2>1. What Information Do We Collect?</h2>
          <h3>Personal information you disclose to us</h3>
          <p><em>In Short: We collect personal information that you provide to us.</em></p>
          <p>
            We collect personal information that you voluntarily provide to us when you express an interest in
            obtaining information about us or our products and Services, when you participate in activities on
            the Services, or otherwise when you contact us.
          </p>
          <p>The personal information we collect may include the following:</p>
          <ul>
            <li>Names</li>
            <li>Phone numbers</li>
            <li>Email addresses</li>
            <li>Usernames</li>
            <li>Passwords</li>
            <li>Contact or authentication data</li>
          </ul>
          <p><strong>Sensitive Information.</strong> We do not process sensitive information.</p>
          <h3>Application Data</h3>
          <p>When you use our mobile application, we may also collect the following information automatically:</p>
          <ul>
            <li>
              <strong>Device Interaction Data.</strong> Actions performed within the app, such as building
              automation commands (device toggles, temperature set-points, mode changes), timestamps of
              interactions, and device state changes. This data is used to operate the smart building platform
              and provide real-time control to authorised users.
            </li>
            <li>
              <strong>Usage Data.</strong> Features accessed, session timestamps, and error logs. This is used
              for service operation, troubleshooting, and platform improvement.
            </li>
          </ul>
          <p>
            <strong>Data We Do Not Collect.</strong> The application does not request or collect location data,
            camera or microphone access, contacts, health or fitness data, or any other device permissions beyond
            those necessary to operate the smart building control features described above.
          </p>
          <p>
            All personal information that you provide to us must be true, complete, and accurate, and you must
            notify us of any changes to such personal information.
          </p>
        </div>

        <div className="policy-section" id="section-2">
          <h2>2. How Do We Process Your Information?</h2>
          <p><em>In Short: We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law.</em></p>
          <p>We process your personal information for a variety of reasons, depending on how you interact with our Services, including:</p>
          <ul>
            <li><strong>To deliver and facilitate delivery of services.</strong> We process your information to authenticate your identity, authorise access to building systems, and execute device control commands on your behalf.</li>
            <li><strong>To manage and improve our Services.</strong> We use usage and interaction data to monitor platform performance, diagnose errors, and improve the reliability and features of the Services.</li>
            <li><strong>To communicate with you.</strong> We may use your contact information to send important notices about your account, security alerts, or changes to our Services.</li>
            <li><strong>For security and fraud prevention.</strong> We process authentication data to detect unauthorised access attempts and protect the integrity of the platform.</li>
            <li><strong>To comply with legal obligations.</strong> We may process your information where required by applicable law or to respond to lawful requests from public authorities.</li>
          </ul>
        </div>

        <div className="policy-section" id="section-3">
          <h2>3. When and With Whom Do We Share Your Personal Information?</h2>
          <p><em>In Short: We may share information in specific situations described in this section and/or with the following third parties.</em></p>
          <p>We may need to share your personal information in the following situations:</p>
          <ul>
            <li>
              <strong>Business Transfers.</strong> We may share or transfer your information in connection with,
              or during negotiations of, any merger, sale of company assets, financing, or acquisition of all
              or a portion of our business to another company.
            </li>
          </ul>
        </div>

        <div className="policy-section" id="section-4">
          <h2>4. How Long Do We Keep Your Information?</h2>
          <p><em>In Short: We keep your information for as long as necessary to fulfill the purposes outlined in this Privacy Notice unless otherwise required by law.</em></p>
          <p>
            We will only keep your personal information for as long as it is necessary for the purposes set out
            in this Privacy Notice, unless a longer retention period is required or permitted by law. No purpose
            in this notice will require us keeping your personal information for longer than 2 years.
          </p>
          <p>
            When we have no ongoing legitimate business need to process your personal information, we will either
            delete or anonymize such information, or, if this is not possible, then we will securely store your
            personal information and isolate it from any further processing until deletion is possible.
          </p>
        </div>

        <div className="policy-section" id="section-5">
          <h2>5. How Do We Keep Your Information Safe?</h2>
          <p><em>In Short: We aim to protect your personal information through a system of organizational and technical security measures.</em></p>
          <p>
            We have implemented appropriate and reasonable technical and organizational security measures designed
            to protect the security of any personal information we process. However, despite our safeguards and
            efforts to secure your information, no electronic transmission over the Internet or information storage
            technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers,
            cybercriminals, or other unauthorized third parties will not be able to defeat our security and
            improperly collect, access, steal, or modify your information. Although we will do our best to protect
            your personal information, transmission of personal information to and from our Services is at your own
            risk. You should only access the Services within a secure environment.
          </p>
        </div>

        <div className="policy-section" id="section-6">
          <h2>6. Do We Collect Information From Minors?</h2>
          <p><em>In Short: We do not knowingly collect data from or market to children under 18 years of age.</em></p>
          <p>
            We do not knowingly collect, solicit data from, or market to children under 18 years of age, nor do
            we knowingly sell such personal information. By using the Services, you represent that you are at least
            18 or that you are the parent or guardian of such a minor and consent to such minor dependent's use of
            the Services. If we learn that personal information from users less than 18 years of age has been
            collected, we will deactivate the account and take reasonable measures to promptly delete such data
            from our records. If you become aware of any data we may have collected from children under age 18,
            please contact us at <a href="mailto:info@manzilismart.com">info@manzilismart.com</a>.
          </p>
        </div>

        <div className="policy-section" id="section-7">
          <h2>7. What Are Your Privacy Rights?</h2>
          <p><em>In Short: Depending on your location, you may have the right to access, correct, delete, or restrict how we use your personal information.</em></p>
          <p>Depending on the applicable laws of your country, you may have the following rights regarding your personal information:</p>
          <ul>
            <li><strong>Right to access.</strong> You may request a copy of the personal information we hold about you.</li>
            <li><strong>Right to correction.</strong> You may ask us to correct inaccurate or incomplete personal information.</li>
            <li><strong>Right to deletion.</strong> You may request that we delete your personal information, subject to certain legal exceptions.</li>
            <li><strong>Right to restriction.</strong> You may ask us to restrict the processing of your information in certain circumstances.</li>
            <li><strong>Right to withdraw consent.</strong> Where we rely on your consent to process your information, you may withdraw that consent at any time. Withdrawal will not affect the lawfulness of any processing that occurred before the withdrawal.</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us at{' '}
            <a href="mailto:info@manzilismart.com">info@manzilismart.com</a>.
            We will consider and act upon any request in accordance with applicable data protection laws.
          </p>
        </div>

        <div className="policy-section" id="section-8">
          <h2>8. Controls for Do-Not-Track Features</h2>
          <p>
            Most web browsers and some mobile operating systems and mobile applications include a Do-Not-Track
            ("DNT") feature or setting you can activate to signal your privacy preference not to have data about
            your online browsing activities monitored and collected. At this stage, no uniform technology standard
            for recognizing and implementing DNT signals has been finalized. As such, we do not currently respond
            to DNT browser signals or any other mechanism that automatically communicates your choice not to be
            tracked online. If a standard for online tracking is adopted that we must follow in the future, we will
            inform you about that practice in a revised version of this Privacy Notice.
          </p>
        </div>

        <div className="policy-section" id="section-9">
          <h2>9. Do We Make Updates to This Notice?</h2>
          <p><em>In Short: Yes, we will update this notice as necessary to stay compliant with relevant laws.</em></p>
          <p>
            We may update this Privacy Notice from time to time. The updated version will be indicated by an updated
            "Revised" date at the top of this Privacy Notice. If we make material changes to this Privacy Notice,
            we may notify you either by prominently posting a notice of such changes or by directly sending you a
            notification. We encourage you to review this Privacy Notice frequently to be informed of how we are
            protecting your information.
          </p>
        </div>

        <div className="policy-section" id="section-10">
          <h2>10. How Can You Contact Us About This Notice?</h2>
          <p>If you have questions or comments about this notice, you may email us at <a href="mailto:info@manzilismart.com">info@manzilismart.com</a> or contact us by post at:</p>
          <address>
            EPO POWER AND CONTROL TRADING<br />
            2nd Floor, Apartment No. 12, Building No. 76<br />
            Zone 27, Street No. 230, C-ring Road<br />
            Doha, Qatar 11926
          </address>
        </div>

        <div className="policy-section" id="section-11">
          <h2>11. How Can You Review, Update, or Delete the Data We Collect From You?</h2>
          <p>
            Based on the applicable laws of your country, you may have the right to request access to the personal
            information we collect from you, details about how we have processed it, correct inaccuracies, or delete
            your personal information. You may also have the right to withdraw your consent to our processing of
            your personal information. These rights may be limited in some circumstances by applicable law. To
            request to review, update, or delete your personal information, please contact us at{' '}
            <a href="mailto:info@manzilismart.com">info@manzilismart.com</a>.
          </p>
        </div>

      </div>
    </div>

    <LandingFooter />
  </div>
);

export default PrivacyPage;
